import { useEffect, useRef, useState } from 'react';

interface CountUpProps {
	end: number;
	duration?: number;
	prefix?: string;
	suffix?: string;
	decimals?: number;
	separator?: string;
}

export default function CountUp({
	end,
	duration = 1.2,
	prefix = '',
	suffix = '',
	decimals = 0,
	separator = ',',
}: CountUpProps) {
	const [display, setDisplay] = useState('0');
	const rafRef = useRef<number>();
	const startTimeRef = useRef<number>();

	useEffect(() => {
		const startValue = 0;
		const durationMs = duration * 1000;

		const format = (n: number) => {
			const fixed = n.toFixed(decimals);
			if (!separator) return fixed;
			const [intPart, decPart] = fixed.split('.');
			const withSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
			return decPart !== undefined ? `${withSep}.${decPart}` : withSep;
		};

		const step = (timestamp: number) => {
			if (!startTimeRef.current) startTimeRef.current = timestamp;
			const elapsed = timestamp - startTimeRef.current;
			const progress = Math.min(elapsed / durationMs, 1);
			// ease-out cubic
			const eased = 1 - Math.pow(1 - progress, 3);
			const current = startValue + (end - startValue) * eased;
			setDisplay(format(current));

			if (progress < 1) {
				rafRef.current = requestAnimationFrame(step);
			}
		};

		startTimeRef.current = undefined;
		rafRef.current = requestAnimationFrame(step);

		return () => {
			if (rafRef.current) cancelAnimationFrame(rafRef.current);
		};
	}, [end, duration, decimals, separator]);

	return <>{prefix}{display}{suffix}</>;
}
